import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Card, Row, Col, Select, Segmented, Table, Tag, Typography, Empty, Alert,
  Badge, Button, Space, Tooltip, App,
} from 'antd';
import { WhatsAppOutlined, ReloadOutlined } from '@ant-design/icons';
import { catalogoGeneticaService, consultaCatalogoService } from '../../services/api';
import { fecha } from '../../utils/format';
import useColumnasResponsive from '../../components/tabla/useColumnasResponsive';

const { Title, Text, Paragraph } = Typography;

// El verde de WhatsApp fijo, igual que en la ficha pública
// (`AnimalCatalogoPage.jsx`): acá representa al canal, no al productor.
const VERDE_WHATSAPP = '#25D366';

const ESTADO_COLOR = { nueva: 'gold', respondida: 'green' };
const CANAL_COLOR = { whatsapp: 'green', interna: 'blue' };
const CANAL_LABEL = { whatsapp: 'WhatsApp', interna: 'Interna' };

/**
 * «Consultas del catálogo»: la bandeja de entrada de lo que preguntan los
 * compradores desde la ficha pública de un animal (botón «Consultar por este
 * animal», tanto por WhatsApp como por mensajería interna).
 *
 * ES PANTALLA PROPIA Y NO UN OCTAVO TAB DEL PANEL DEL CATÁLOGO
 * (`CatalogoGenetica.jsx` ya tiene siete). La diferencia no es de espacio: la
 * config del catálogo (colores, secciones, WhatsApp) se mira una vez cada
 * tanto, cuando se arma o se retoca el sitio; la bandeja de consultas se mira
 * TODOS LOS DÍAS, como el mail. Mezclarlas en el mismo lugar hace que abrir
 * "el catálogo" para ver quién preguntó obligue a navegar entre tabs de
 * diseño — el mismo argumento por el que las métricas del catálogo se
 * mudaron a `Analytics/MetricasCatalogo.jsx` (ver el comentario de ese
 * archivo).
 */
const ConsultasCatalogo = () => {
  const { message } = App.useApp();

  const [catalogos, setCatalogos] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  // 'todos' es un centinela y no `undefined`: con `undefined` el <Select>
  // queda en modo "sin elegir" (muestra el placeholder) en vez de mostrar la
  // opción "Todos los catálogos" seleccionada.
  const [idCatalogo, setIdCatalogo] = useState('todos');

  const [estadoFiltro, setEstadoFiltro] = useState('nueva');
  const [consultas, setConsultas] = useState([]);
  const [nuevas, setNuevas] = useState(0);
  const [loading, setLoading] = useState(true);
  // 'sin_permiso' (403 — el plan no incluye el recurso) vs 'server_error'
  // (todo lo demás). Mismo criterio que `MetricasCatalogo.jsx`: un 403 no es
  // "algo se rompió", es "tu plan no incluye esto".
  const [error, setError] = useState(null);
  const [actualizandoId, setActualizandoId] = useState(null);
  // `total` es el conteo SIN el tope de 500 del backend. Se guarda para poder
  // decir "mostrando 500 de 812" en vez de que las más viejas desaparezcan
  // en silencio: misma clase de 200 incompleto que `utils/listaCorta.js`
  // existe para avisar.
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await catalogoGeneticaService.getAll();
        if (vivo) setCatalogos(data.catalogos || []);
      } catch (err) {
        // NO silencioso: si esto falla, el productor pierde el filtro por
        // catálogo y un selector vacío sin aviso es un control que miente.
        // La bandeja igual se carga entera (es otro endpoint), y el texto
        // lo dice. Mismo criterio que el filtro de `CatalogoGenetica.jsx`.
        if (vivo) {
          message.error(
            err.response?.data?.error
            || 'No se pudieron cargar los catálogos para filtrar. Podés ver todas las consultas igual.',
          );
        }
      } finally {
        if (vivo) setLoadingCatalogos(false);
      }
    })();
    return () => { vivo = false; };
    // `message` sale de `App.useApp()` y es estable: va en las deps para
    // que el linter no lo marque, y no re-dispara el efecto.
  }, [message]);

  const cargarConsultas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await consultaCatalogoService.getAll({
        id_catalogo: idCatalogo === 'todos' ? undefined : idCatalogo,
        estado: estadoFiltro === 'todas' ? undefined : estadoFiltro,
      });
      setConsultas(data.consultas || []);
      setNuevas(data.nuevas ?? 0);
      setTotal(data.total ?? (data.consultas || []).length);
    } catch (err) {
      // Sin `message.error`: excepción (e) de react-rules.md. El error se
      // pinta como Alert PERSISTENTE donde iría la tabla, distinguiendo 403
      // ("tu plan no incluye esto") de 500. Mismo criterio que
      // `MetricasCatalogo.jsx`.
      setConsultas([]);
      setError(err.response?.status === 403 ? 'sin_permiso' : 'server_error');
    } finally {
      setLoading(false);
    }
  }, [idCatalogo, estadoFiltro]);

  useEffect(() => { cargarConsultas(); }, [cargarConsultas]);

  // Marca/desmarca SIN recargar la tabla entera. Si el filtro activo es
  // 'nueva' o 'respondida' y el nuevo estado deja de coincidir, la fila se
  // saca de la lista local: dejarla mostraría, por ejemplo, una consulta
  // "respondida" adentro de la pestaña "Nuevas".
  const cambiarEstado = async (record, nuevoEstado) => {
    setActualizandoId(record.id_consulta);
    try {
      const { data } = await consultaCatalogoService.setEstado(record.id_consulta, nuevoEstado);
      setConsultas((prev) => {
        // MERGE y no reemplazo: el PUT devuelve el dict pelado del modelo,
        // y las claves que el listado agrega a mano (`nombre_catalogo`,
        // `slug_catalogo`, `slug_animal`) no vienen. Reemplazando la fila
        // entera, en «Todas» + «Todos los catálogos» la columna Catálogo
        // quedaba en blanco y el WhatsApp decía "te escribo de undefined".
        const actualizada = prev.map((c) => (
          c.id_consulta === record.id_consulta ? { ...c, ...data } : c
        ));
        return estadoFiltro === 'todas'
          ? actualizada
          : actualizada.filter((c) => c.estado === estadoFiltro);
      });
      // La fila que sale de la lista filtrada también sale de `total`: si no,
      // con 10 nuevas y una recién respondida quedan 9 filas contra un total
      // de 10 y el aviso de "lista cortada" salta en falso.
      if (estadoFiltro !== 'todas' && nuevoEstado !== estadoFiltro) {
        setTotal((prev) => Math.max(0, prev - 1));
      }
      setNuevas((prev) => {
        if (nuevoEstado === 'respondida' && record.estado === 'nueva') return Math.max(0, prev - 1);
        if (nuevoEstado === 'nueva' && record.estado === 'respondida') return prev + 1;
        return prev;
      });
      message.success(
        nuevoEstado === 'respondida' ? 'Marcada como respondida' : 'Vuelve a "nueva"',
      );
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos actualizar la consulta');
    } finally {
      setActualizandoId(null);
    }
  };

  const todosCatalogos = idCatalogo === 'todos';

  const columnas = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_alta',
      key: 'fecha',
      render: (v) => (
        <span>
          {fecha(v)}
          {v && <Text type="secondary" style={{ marginLeft: 6 }}>{dayjs(v).format('HH:mm')}</Text>}
        </span>
      ),
    },
    // Sólo con "Todos los catálogos": elegido UNO, la columna repetiría el
    // mismo nombre en cada fila.
    ...(todosCatalogos ? [{
      title: 'Catálogo',
      dataIndex: 'nombre_catalogo',
      key: 'catalogo',
    }] : []),
    {
      title: 'Animal',
      key: 'animal',
      render: (_, r) => (r.slug_animal ? (
        <a href={`/genetica/${r.slug_catalogo}/animal/${r.slug_animal}`} target="_blank" rel="noopener noreferrer">
          {r.nombre_animal}
        </a>
      ) : (r.nombre_animal || <Text type="secondary">—</Text>)),
    },
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre' },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      render: (v, r) => {
        // El texto lleva el nombre del catálogo y del animal: la cabaña abre
        // WhatsApp y ya sabe de qué se trata, sin repreguntar.
        const texto = `Hola ${r.nombre}, te escribo de ${r.nombre_catalogo} por tu consulta sobre ${r.nombre_animal || 'nuestros animales'}.`;
        return (
          <Space size={6}>
            <Text>{v}</Text>
            <Tooltip title="Escribir por WhatsApp">
              <Button
                size="small"
                shape="circle"
                icon={<WhatsAppOutlined />}
                style={{ background: VERDE_WHATSAPP, borderColor: VERDE_WHATSAPP, color: '#fff' }}
                href={`https://wa.me/${v}?text=${encodeURIComponent(texto)}`}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Mensaje',
      dataIndex: 'mensaje',
      key: 'mensaje',
      render: (v) => (v
        ? <Paragraph style={{ marginBottom: 0, maxWidth: 260 }} ellipsis={{ rows: 2, expandable: true, symbol: 'ver más' }}>{v}</Paragraph>
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Canal',
      dataIndex: 'canal',
      key: 'canal',
      render: (v) => <Tag color={CANAL_COLOR[v] || 'default'}>{CANAL_LABEL[v] || v}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (v) => <Tag color={ESTADO_COLOR[v] || 'default'}>{v === 'nueva' ? 'Nueva' : 'Respondida'}</Tag>,
    },
    {
      title: '',
      key: 'accion',
      render: (_, r) => (
        <Button
          size="small"
          loading={actualizandoId === r.id_consulta}
          onClick={() => cambiarEstado(r, r.estado === 'nueva' ? 'respondida' : 'nueva')}
        >
          {r.estado === 'nueva' ? 'Marcar respondida' : 'Volver a nueva'}
        </Button>
      ),
    },
  ];

  const { columnas: colsResponsivas, propsTabla } = useColumnasResponsive({
    desktop: columnas,
    mobile: {
      title: 'Consulta',
      principal: 'nombre',
      secundarios: [
        { key: 'fecha' },
        ...(todosCatalogos ? [{ key: 'catalogo', etiqueta: 'Catálogo' }] : []),
        { key: 'animal', etiqueta: 'Animal' },
        { key: 'telefono' },
        { key: 'canal' },
        { key: 'estado' },
      ],
      conservar: [{ key: 'accion', width: 130 }],
    },
    scroll: { x: 'max-content' },
  });

  const textoVacio = () => {
    if (estadoFiltro === 'nueva') return 'No tenés consultas sin responder';
    if (estadoFiltro === 'respondida') return 'Todavía no respondiste ninguna consulta';
    return 'Todavía no llegó ninguna consulta';
  };

  return (
    <div style={{ padding: 24 }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Consultas del catálogo</Title>
          <Text type="secondary">Lo que preguntan los compradores desde la ficha pública de cada animal.</Text>
        </Col>
        <Col>
          <Space wrap>
            <Select
              style={{ minWidth: 220 }}
              value={idCatalogo}
              onChange={setIdCatalogo}
              loading={loadingCatalogos}
              options={[
                { value: 'todos', label: 'Todos los catálogos' },
                ...catalogos.map((c) => (
                  { value: c.id_catalogo_genetica, label: c.nombre_catalogo }
                )),
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={cargarConsultas} loading={loading}>
              Actualizar
            </Button>
          </Space>
        </Col>
      </Row>

      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col>
          <Segmented
            value={estadoFiltro}
            onChange={setEstadoFiltro}
            options={[
              { label: 'Nuevas', value: 'nueva' },
              { label: 'Respondidas', value: 'respondida' },
              { label: 'Todas', value: 'todas' },
            ]}
          />
        </Col>
        <Col>
          <Space size={16}>
            <Text>
              Mostrando <strong>{consultas.length}</strong>
              {total > consultas.length && (
                // La lista vino CORTADA por el tope del backend: se dice, y
                // se sugiere el camino (filtrar) para llegar a las que faltan.
                <Text type="warning"> de {total}: filtrá por catálogo o estado para ver las más viejas</Text>
              )}
            </Text>
            {/* El badge de "sin responder" se ve SIEMPRE, no sólo en la
                pestaña "Nuevas": es el aviso de que hay algo pendiente aunque
                el productor esté mirando otra cosa. */}
            <Badge
              count={nuevas}
              showZero
              color={nuevas > 0 ? '#faad14' : '#d9d9d9'}
              style={{ color: nuevas > 0 ? undefined : '#00000073' }}
            />
            <Text type="secondary">sin responder</Text>
          </Space>
        </Col>
      </Row>

      {error === 'sin_permiso' ? (
        <Alert
          type="info"
          showIcon
          message="Tu plan no incluye el catálogo de genética"
          description="Cuando actives ese módulo vas a poder ver acá las consultas de tus compradores."
        />
      ) : error === 'server_error' ? (
        <Alert
          type="error"
          showIcon
          message="No pudimos cargar las consultas"
          description="Puede ser algo pasajero. Probá con «Actualizar» en un momento."
        />
      ) : (
        <Card>
          {!loading && consultas.length === 0 ? (
            <Empty description={textoVacio()} />
          ) : (
            <Table
              {...propsTabla}
              columns={colsResponsivas}
              dataSource={consultas}
              rowKey="id_consulta"
              loading={loading}
              pagination={{ pageSize: 20 }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default ConsultasCatalogo;
